import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useLoggedInEmployeeId, useLoggedInUserRoles } from '~/components/Auth';
import { useEmployeeItemInfo } from '~/components/GenericList/EmployeeItemInfo';
import { OrganizationUnit } from '~/data/orgunits';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';
import { OrgUnitPopupProps } from '~/scenes/PortalViews/Views/PortalAltMgr/components/OrgUnitPopup/component';
import {
  convertFormDataToOrganizationUnit,
  convertOrganizationUnitToFormData,
} from '~/scenes/PortalViews/Views/PortalAltMgr/components/OrgUnitPopup/data';
import {
  OrgUnitFormData,
  OrgUnitPopupMode,
  OrganizationUnitSubmission,
} from '~/scenes/PortalViews/Views/PortalAltMgr/components/OrgUnitPopup/types';
import { useValidationSchema } from '~/scenes/PortalViews/Views/PortalAltMgr/components/OrgUnitPopup/useValidationSchema';
import { fetchFromOldApi } from '~/services/Http';
import { isRoleDevHR, isRoleHR, isRoleManager } from '~/services/Permissions';

export const useOrgUnitPopup = (props: OrgUnitPopupProps) => {
  const {
    selectedUnit,
    handleClose,
    handleCreateUnit,
    handleUpdateUnit,
    handleDeleteUnit,
    managerIds,
    teamManagerIds,
    hrResponsibleIds,
    alternateMgrIds,
  } = props;

  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const loggedInEmployeeId = useLoggedInEmployeeId();
  const loggedInUserRoles = useLoggedInUserRoles();

  const [popupMode, setPopupMode] = useState<OrgUnitPopupMode>(
    OrgUnitPopupMode.Read,
  );

  // User access level & permissions
  const { userEditMode, userCanAddNew, userCanEdit } = useMemo(() => {
    const isHR = isRoleHR(loggedInUserRoles);
    const isDevHR = isRoleDevHR(loggedInUserRoles);
    const isManager = isRoleManager(loggedInUserRoles);

    const resolveUserEditMode = (): OrgUnitPopupMode => {
      const isMyManagerUnit =
        isManager && selectedUnit?.fOrgUnitManager === loggedInEmployeeId;

      if (!isDevHR && isHR) {
        return OrgUnitPopupMode.Edit;
      } else if (isManager && isMyManagerUnit) {
        return OrgUnitPopupMode.SubstituteEdit;
      }

      return OrgUnitPopupMode.Read;
    };

    const userEditMode = resolveUserEditMode();

    return {
      userCanAddNew: userEditMode === OrgUnitPopupMode.Edit,
      userCanEdit: userEditMode !== OrgUnitPopupMode.Read,
      userEditMode,
    };
  }, [selectedUnit, loggedInEmployeeId, loggedInUserRoles]);

  const { getEmployeeItemInfoByIds } = useEmployeeItemInfo();
  const managers = getEmployeeItemInfoByIds(managerIds);
  const teamManagers = getEmployeeItemInfoByIds(teamManagerIds);
  const hrResponsibles = getEmployeeItemInfoByIds(hrResponsibleIds);
  const alternateMgrs = React.useMemo(() => {
    return getEmployeeItemInfoByIds(alternateMgrIds);
  }, [alternateMgrIds, getEmployeeItemInfoByIds]);

  const emptyFormData: OrgUnitFormData = useMemo(() => {
    return {
      fOrgUnitName: '',
      fOrgUnitNumber: '',
      fOrgUnitType: '',
      fOrgUnitLoc: 0,
      fOrgUnitUrl: '',
      fOrgUnitCreated: '',
      fOrgUnitAvail: '',
      fOrgUnitManager: -1,
      fOrgUnitResponsibleEmps: [],
      fOrgUnitContainingTrees: [],
      fOrgUnitCostCenters: [],
      substituteManager: null,
      substituteStartDate: '',
      substituteEndDate: '',
      alternateManagers: [],
      responsibleHRs: [],
      deputyManagers: [],
    };
  }, []);

  const validationSchema = useValidationSchema();

  const resolver = useYupValidationResolver(
    validationSchema,
    'react-hook-form',
  );

  const formMethods = useForm<OrgUnitFormData>({
    defaultValues: emptyFormData,
    resolver,
  });

  const { reset, handleSubmit } = formMethods;

  const resetToEmpty = useCallback(
    () => reset(emptyFormData),
    [reset, emptyFormData],
  );

  const resetToSelectedUnit = useCallback(
    (selectedUnit: OrganizationUnit) => {
      reset(convertOrganizationUnitToFormData(selectedUnit, alternateMgrs));
    },
    [reset, alternateMgrs],
  );

  useEffect(() => {
    if (selectedUnit) {
      resetToSelectedUnit(selectedUnit);
    }
  }, [selectedUnit, resetToSelectedUnit]);

  useEffect(() => {
    if (userCanAddNew && !selectedUnit) {
      setPopupMode(OrgUnitPopupMode.New);
    } else if (popupMode === OrgUnitPopupMode.New && !!selectedUnit) {
      setPopupMode(OrgUnitPopupMode.Read);
    }
  }, [userCanAddNew, selectedUnit, popupMode, setPopupMode]);

  const handleOrgUnitSubmission = async (body: OrganizationUnitSubmission) => {
    const unitId = body.fOrgUnitId;
    const isUpdate = typeof unitId !== 'undefined';
    const method = isUpdate ? 'PUT' : 'POST';
    const path = `/form/org/unit${isUpdate ? `/${unitId}` : ''}`;

    await fetchFromOldApi(path, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then((r) => r.json())
      .then((data) => {
        if (isUpdate) {
          handleUpdateUnit(data);
          enqueueSnackbar(t('change_saved'), { variant: 'success' });
        } else {
          handleCreateUnit(data);
          enqueueSnackbar(t('laCreated'), { variant: 'success' });
        }
        onCloseDialog();
      })
      .catch((_err) => {
        enqueueSnackbar(t('laFailedToSave'), { variant: 'error' });
      });
  };

  const onEditStart = useCallback(() => {
    if (userCanEdit) {
      setPopupMode(userEditMode);
    }
  }, [userEditMode, userCanEdit, setPopupMode]);

  const onEditCancel = useCallback(() => {
    setPopupMode(OrgUnitPopupMode.Read);
    if (selectedUnit) {
      resetToSelectedUnit(selectedUnit);
    } else {
      reset(emptyFormData);
    }
  }, [setPopupMode, reset, resetToSelectedUnit, selectedUnit, emptyFormData]);

  const onCloseDialog = useCallback(
    (event = null, reason = null) => {
      if (reason === 'backdropClick') {
        // Prevent closing when clicking outside
        return;
      }
      setPopupMode(OrgUnitPopupMode.Read);
      resetToEmpty();
      handleClose();
    },
    [resetToEmpty, handleClose, setPopupMode],
  );

  const handleSave = async (data: OrgUnitFormData) => {
    if (selectedUnit) {
      await handleOrgUnitSubmission(
        convertFormDataToOrganizationUnit(data, selectedUnit),
      );
    } else {
      await handleOrgUnitSubmission(convertFormDataToOrganizationUnit(data));
    }
  };

  const handleOrgUnitDelete = async () => {
    if (selectedUnit) {
      try {
        await fetchFromOldApi(`/json/org/unit/${selectedUnit.fOrgUnitId}`, {
          method: 'DELETE',
        });
        enqueueSnackbar(t('laDeleted'), { variant: 'success' });
        handleDeleteUnit(selectedUnit);
        onCloseDialog();
      } catch (error) {
        enqueueSnackbar(t('laFailedToSave'), { variant: 'error' });
      }
    }
  };

  const isFieldDisabled = useCallback(
    (specialEditModes?: OrgUnitPopupMode[]) => {
      const defaultEditableModes = [
        OrgUnitPopupMode.New,
        OrgUnitPopupMode.Edit,
      ];

      return !(
        !!specialEditModes
          ? [...defaultEditableModes, ...specialEditModes]
          : defaultEditableModes
      ).includes(popupMode);
    },
    [popupMode],
  );

  const onSubmit = handleSubmit(handleSave);

  return {
    formMethods,
    managers,
    teamManagers,
    hrResponsibles,
    alternateMgrs,
    isFieldDisabled,
    mode: {
      isEditMode: popupMode !== OrgUnitPopupMode.Read,
      popupMode,
    },
    permission: {
      userCanEdit,
      userCanDelete: !!selectedUnit && userCanAddNew,
    },
    actions: {
      onEditStart,
      onEditCancel,
      onSubmit,
      onCloseDialog,
      onDeleteUnit: handleOrgUnitDelete,
    },
  };
};
